'use client';

import { useEffect, useRef, useState, type ReactNode, type SubmitEvent } from 'react';
import { toast } from 'sonner';
import { AlertCircle, ArrowUpRight, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import {
  attributionToFormFields,
  attributionToVaData,
  getAttribution,
} from '@/lib/analytics/attribution';
import { VA_EVENTS } from '@/lib/analytics/event-catalog';
import { gtmEvent, metaTrack } from '@/lib/analytics/marketing-events';
import { vaTrack } from '@/lib/analytics/vercel-events';
import { useIsCompactTouch } from '@/hooks/use-compact-touch';
import type { ContactSectionContent } from '@/sanity/lib/sections';
import { sanityTextToPlainText } from '@/components/shared/sanity-text';

export type LeadFormStatus = 'idle' | 'submitting' | 'error';

function getFormCopy(content?: ContactSectionContent | null) {
  const form = content?.form;
  const fields = form?.fields;
  const fieldCopy = (
    value?: { label?: string | null; placeholder?: string | null; error?: string | null } | null,
  ) => ({
    label: value?.label ?? '',
    placeholder: value?.placeholder ?? '',
    error: value?.error ?? '',
  });

  return {
    fields: {
      name: fieldCopy(fields?.name),
      email: fieldCopy(fields?.email),
      phone: fieldCopy(fields?.phone),
      projectType: {
        ...fieldCopy(fields?.projectType),
        placeholder: 'e.g. Hotel renovation',
      },
      message: fieldCopy(fields?.message),
    },
    submit: {
      idle: form?.submitIdle ?? '',
      loading: form?.submitLoading ?? '',
    },
    error: form?.error ?? '',
    successSonner: {
      title: form?.successSonner?.title ?? '',
      description: form?.successSonner?.description ?? '',
    },
  };
}

export type LeadFormProps = {
  content?: ContactSectionContent | null;
  bare?: boolean;
  className?: string;
  cardClassName?: string;
  idPrefix?: string;
  /** When set (e.g. form inside dialog/drawer), call after success so the overlay can close before the toast runs. */
  scheduleSuccessToastAction?: (showToast: () => void) => void;
};

export function LeadForm({
  content,
  bare = false,
  className,
  cardClassName,
  idPrefix = 'lead',
  scheduleSuccessToastAction,
}: LeadFormProps) {
  const [status, setStatus] = useState<LeadFormStatus>('idle');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [projectType, setProjectType] = useState('');
  const [message, setMessage] = useState('');
  const nameId = `${idPrefix}-name`;
  const emailId = `${idPrefix}-email`;
  const phoneId = `${idPrefix}-phone`;
  const subjectId = `${idPrefix}-subject`;
  const messageId = `${idPrefix}-message`;
  const formCopy = getFormCopy(content);
  const isOverlayForm = Boolean(scheduleSuccessToastAction);
  const fieldSurfaceClass = cn(isOverlayForm ? 'bg-neutral-50' : 'bg-white', 'dark:bg-neutral-800');
  const requiredFieldsComplete =
    name.trim().length > 0 &&
    email.trim().length > 0 &&
    projectType.trim().length > 0 &&
    message.trim().length > 0;

  function resetLeadForm() {
    setStatus('idle');
    setName('');
    setEmail('');
    setPhone('');
    setProjectType('');
    setMessage('');
  }

  async function handleSubmit(e: SubmitEvent<HTMLFormElement>) {
    e.preventDefault();
    setStatus('submitting');
    const data = Object.fromEntries(new FormData(e.currentTarget)) as Record<string, string>;
    Object.assign(data, attributionToFormFields(getAttribution()));

    const projectTypeValue = data.subject?.trim() || 'General Inquiry';
    vaTrack(VA_EVENTS.LEAD_FORM_SUBMIT_ATTEMPT, {
      project_type: projectTypeValue,
      has_phone: Boolean(data.phone?.trim()),
      form_context: idPrefix,
      ...attributionToVaData(),
    });

    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        vaTrack(VA_EVENTS.LEAD_FORM_SUBMIT_ERROR, {
          status_code: res.status,
          form_context: idPrefix,
          ...attributionToVaData(),
        });
        throw new Error();
      }
      const successTitle = formCopy.successSonner.title.trim() || 'Message sent';
      const successDescription = formCopy.successSonner.description.trim();
      const showSuccessToast = () => {
        toast.success(
          successTitle,
          successDescription ? { description: successDescription } : undefined,
        );
      };
      gtmEvent({ event: 'lead_form_submission' });
      metaTrack('Lead');
      resetLeadForm();
      if (scheduleSuccessToastAction) {
        scheduleSuccessToastAction(showSuccessToast);
      } else {
        showSuccessToast();
      }
    } catch {
      setStatus('error');
    }
  }

  const inner = (
    <form onSubmit={handleSubmit} className={cn('flex flex-col gap-6', className)}>
      <div className="grid gap-4 sm:gap-5 sm:grid-cols-2">
        <FormField id={nameId} label={formCopy.fields.name.label} required>
          <Input
            id={nameId}
            name="name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder={formCopy.fields.name.placeholder}
            required
            autoComplete="name"
            className={cn(
              'h-11 hover:border-[#948B7F] focus-visible:border-[#948B7F] focus-visible:ring-[#948B7F] focus-visible:ring-offset-0 focus-visible:ring-1 caret-[#948B7F]',
              fieldSurfaceClass,
            )}
          />
        </FormField>
        <FormField id={emailId} label={formCopy.fields.email.label} required>
          <Input
            id={emailId}
            name="email"
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder={formCopy.fields.email.placeholder}
            required
            autoComplete="email"
            className={cn(
              'h-11 hover:border-[#948B7F] focus-visible:border-[#948B7F] focus-visible:ring-[#948B7F] focus-visible:ring-offset-0 focus-visible:ring-1 caret-[#948B7F]',
              fieldSurfaceClass,
            )}
          />
        </FormField>
      </div>

      <div className="grid gap-5 sm:grid-cols-2">
        <FormField id={phoneId} label={formCopy.fields.phone.label}>
          <Input
            id={phoneId}
            name="phone"
            type="tel"
            value={phone}
            onChange={(event) => setPhone(event.target.value)}
            placeholder={formCopy.fields.phone.placeholder}
            autoComplete="tel"
            className={cn(
              'h-11 hover:border-[#948B7F] focus-visible:border-[#948B7F] focus-visible:ring-[#948B7F] focus-visible:ring-offset-0 focus-visible:ring-1 caret-[#948B7F]',
              fieldSurfaceClass,
            )}
          />
        </FormField>
        <FormField id={subjectId} label={formCopy.fields.projectType.label} required>
          <Input
            id={subjectId}
            name="subject"
            value={projectType}
            onChange={(event) => setProjectType(event.target.value)}
            placeholder={formCopy.fields.projectType.placeholder}
            required
            className={cn(
              'h-11 hover:border-[#948B7F] focus-visible:border-[#948B7F] focus-visible:ring-[#948B7F] focus-visible:ring-offset-0 focus-visible:ring-1 caret-[#948B7F]',
              fieldSurfaceClass,
            )}
          />
        </FormField>
      </div>

      <FormField id={messageId} label={formCopy.fields.message.label} required>
        <Textarea
          id={messageId}
          name="message"
          value={message}
          onChange={(event) => setMessage(event.target.value)}
          placeholder={formCopy.fields.message.placeholder}
          required
          className={cn(
            'min-h-32 hover:border-[#948B7F] focus-visible:border-[#948B7F] focus-visible:ring-[#948B7F] focus-visible:ring-offset-0 focus-visible:ring-1 caret-[#948B7F]',
            fieldSurfaceClass,
          )}
        />
      </FormField>

      {status === 'error' && (
        <div className="flex items-center gap-2.5 rounded-lg bg-destructive/10 px-4 py-3 text-sm text-destructive">
          <AlertCircle className="size-4 shrink-0" />
          {formCopy.error}
        </div>
      )}

      <Button
        type="submit"
        size="lg"
        disabled={status === 'submitting' || !requiredFieldsComplete}
        className="group min-h-11 h-11 w-full sm:w-fit rounded-lg sm:rounded-full px-4 bg-[#948B7F] text-white transition-all duration-300 ease-in-out cursor-pointer hover:bg-[#BFB7A1] hover:text-white"
      >
        {status === 'submitting' ? (
          <>
            <Loader2 className="size-4 shrink-0 animate-spin" aria-hidden />
            {formCopy.submit.loading}
          </>
        ) : (
          <>
            {formCopy.submit.idle}
            <ArrowUpRight className="size-4 shrink-0 transition-transform duration-300 ease-in-out group-hover:rotate-45" />
          </>
        )}
      </Button>
    </form>
  );

  if (bare) {
    return <div className={cn('p-6', cardClassName)}>{inner}</div>;
  }

  return (
    <Card className={cn('border border-border bg-card shadow-sm', cardClassName)}>
      <CardContent className="p-6">{inner}</CardContent>
    </Card>
  );
}

export function PrincipalContactDialog({ content }: { content?: ContactSectionContent | null }) {
  const [open, setOpen] = useState(false);
  const isCompactTouch = useIsCompactTouch();
  const successToastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sanityTitle = sanityTextToPlainText(content?.header?.heading).trim();
  const title = sanityTitle;
  const description = content?.header?.subheading ?? '';
  const principalCtaLabel = content?.ctaButtons?.primary?.label;
  const secondaryCtaLabel = content?.ctaButtons?.secondary?.label;

  useEffect(() => {
    function handleClick(event: MouseEvent) {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const leadFormTrigger = target.closest('[data-lead-form-trigger="true"]');
      const trigger = leadFormTrigger ?? target.closest('a, button, [role="button"]');
      if (!trigger) return;

      const isLeadFormTrigger = Boolean(leadFormTrigger);
      const matchesContactCta =
        (principalCtaLabel && trigger.textContent?.trim() === principalCtaLabel) ||
        (secondaryCtaLabel && trigger.textContent?.trim() === secondaryCtaLabel);

      if (!isLeadFormTrigger && !matchesContactCta) return;

      event.preventDefault();
      event.stopPropagation();

      const triggerLocation =
        leadFormTrigger?.getAttribute('data-tracking-location')?.trim() ||
        trigger.getAttribute('data-tracking-location')?.trim() ||
        'unknown';

      vaTrack(VA_EVENTS.LEAD_FORM_OPEN, { trigger_location: triggerLocation });
      setOpen(true);
    }

    document.addEventListener('click', handleClick, true);
    return () => document.removeEventListener('click', handleClick, true);
  }, [principalCtaLabel, secondaryCtaLabel]);

  useEffect(
    () => () => {
      if (successToastTimerRef.current !== null) {
        clearTimeout(successToastTimerRef.current);
      }
    },
    [],
  );

  if (!content || content.show === false || content.showForm === false) {
    return null;
  }

  const form = (
    <LeadForm
      content={content}
      bare
      idPrefix="principal-contact"
      className="gap-4 sm:gap-5"
      cardClassName="p-0 sm:p-0 lg:p-0"
      scheduleSuccessToastAction={(showToast) => {
        setOpen(false);
        if (successToastTimerRef.current !== null) clearTimeout(successToastTimerRef.current);
        successToastTimerRef.current = setTimeout(() => {
          successToastTimerRef.current = null;
          showToast();
        }, 350);
      }}
    />
  );

  if (isCompactTouch) {
    return (
      <Drawer open={open} onOpenChange={setOpen}>
        <DrawerContent className="max-h-[92vh] rounded-t-3xl dark:bg-neutral-950 dark:border-t dark:border-neutral-700">
          <DrawerHeader className="px-6 pb-3 text-left">
            <DrawerTitle className="text-xl text-left">{title}</DrawerTitle>
            <DrawerDescription className="text-left font-light">{description}</DrawerDescription>
          </DrawerHeader>
          <div className="overflow-y-auto p-6">{form}</div>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-h-[90vh] overflow-y-auto p-6 sm:max-w-2xl lg:max-w-3xl">
        <DialogHeader className="pr-8">
          <DialogTitle className="text-2xl text-left">{title}</DialogTitle>
          <DialogDescription className="text-left text-pretty">{description}</DialogDescription>
        </DialogHeader>
        {form}
      </DialogContent>
    </Dialog>
  );
}

export function FormField({
  id,
  label,
  required,
  children,
}: {
  id: string;
  label: string;
  required?: boolean;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-2">
      <label
        htmlFor={id}
        className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground"
      >
        {label}
        {required && <span className="ml-1 text-foreground">*</span>}
      </label>
      {children}
    </div>
  );
}
